import React, { useEffect, useState } from "react";
import {
  Table, Button, Modal, Form, DatePicker, Select, Space, message,
  Divider, InputNumber, Input, Tag, Drawer, Descriptions, Tabs,
  Alert, Radio, Tooltip,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, EyeOutlined, RollbackOutlined,
  CheckCircleOutlined, WarningOutlined, FileTextOutlined, LockOutlined,
} from "@ant-design/icons";
import { sales, customers as custApi, products as prodApi, inventory } from "../api";
import PageHeader from "../components/common/PageHeader";
import StatusBadge from "../components/common/StatusBadge";
import { fmtCurrency, fmtDate } from "../utils/format";
import dayjs from "dayjs";

/**
 * Shows the sale rate that was active on the sale date for this item,
 * with a ✓/⚠ indicator comparing it to what was actually charged.
 */
function SaleRateHistoryCell({ item, saleDate }) {
  const [rateAtDate, setRateAtDate] = useState(undefined);
  const productId = item?.product?.id;
  useEffect(() => {
    if (!productId || !saleDate) { setRateAtDate(null); return; }
    prodApi.getSaleRates(productId).then((res) => {
      const rates = res.data.data || [];
      const d = dayjs(saleDate);
      const match = rates.find((r) => {
        const from = dayjs(r.fromDate);
        const to = r.toDate ? dayjs(r.toDate) : null;
        return (d.isAfter(from, "day") || d.isSame(from, "day"))
          && (to === null || d.isBefore(to, "day") || d.isSame(to, "day"));
      });
      setRateAtDate(match ?? null);
    }).catch(() => setRateAtDate(null));
  }, [productId, saleDate]);

  if (rateAtDate === undefined) return <span style={{ color: "var(--text-muted)" }}>…</span>;
  if (rateAtDate === null) return <span style={{ color: "var(--text-muted)" }}>—</span>;
  const historyRate = Number(rateAtDate.rate);
  const chargedRate = Number(item.rate);
  const matches = Math.abs(historyRate - chargedRate) < 0.01;
  return (
    <Tooltip title={matches ? "Matches rate history" : `History rate: ${fmtCurrency(historyRate)} — differs from charged`}>
      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span className="mono">{fmtCurrency(historyRate)}</span>
        {matches
          ? <CheckCircleOutlined style={{ color: "var(--success, #52c41a)", fontSize: 13 }} />
          : <WarningOutlined style={{ color: "var(--warning, #d46b08)", fontSize: 13 }} />}
      </span>
    </Tooltip>
  );
}

export default function Sales() {
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [viewRec, setViewRec] = useState(null);
  const [returnModal, setReturnModal] = useState(null);
  const [returnLines, setReturnLines] = useState([]);
  const [allCusts, setAllCusts] = useState([]);
  const [allProds, setAllProds] = useState([]);
  const [availQty, setAvailQty] = useState({}); // { productId: availableStock }
  const [lines, setLines] = useState([
    { productId: null, quantity: 1, rate: 0, taxPercent: 0, discount: 0 },
  ]);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [form] = Form.useForm();
  const [retForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const r = await sales.list({ q, page, size: 20 });
      setData(r.data.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [page, q]);
  useEffect(() => {
    custApi.all().then((r) => setAllCusts(r.data.data));
    prodApi.all().then((r) =>
      setAllProds(r.data.data.filter((p) => p.productType === "SALE" || p.productType === "BOTH"))
    );
    // Load available stock for sale products
    inventory.stock().then((r) => {
      const qtyMap = {};
      (r.data.data.saleProducts || []).forEach((p) => {
        qtyMap[p.productId] = Number(p.stock ?? 0);
      });
      setAvailQty(qtyMap);
    });
  }, []);

  const getMax = (productId) => productId ? (availQty[productId] ?? 9999) : 9999;

  // IDs already chosen — prevent duplicates
  const selectedProductIds = lines.map((l) => l.productId).filter(Boolean);

  const updateLine = async (i, f, v) => {
    const l = [...lines];
    l[i][f] = v;
    if (f === "productId" && v) {
      try {
        const res = await prodApi.getSaleRates(v);
        const rates = res.data.data || [];
        const active = rates.find((r) => !r.toDate) || rates[0];
        if (active) {
          l[i].rate       = Number(active.rate);
          l[i].taxPercent = Number(active.taxPercentage || 0);
        }
      } catch { /* keep user-entered values */ }
    }
    setLines(l);
  };

  const lineAmount = (l) => {
    const base = (l.quantity || 0) * ((l.rate || 0) - (l.discount || 0));
    const tax  = base * ((l.taxPercent || 0) / 100);
    return base + tax;
  };

  const total = lines.reduce((s, l) => s + lineAmount(l), 0);

  const onSave = async () => {
    try {
      const vals = await form.validateFields();
      const hasItems = lines.some((l) => l.productId && l.quantity > 0);
      if (!hasItems) { message.error("Add at least one item"); return; }

      // Duplicate product check
      const ids = lines.filter((l) => l.productId).map((l) => l.productId);
      if (new Set(ids).size !== ids.length) {
        message.error("Each product can only appear once. Remove duplicates.");
        return;
      }

      // Stock availability check
      for (const l of lines.filter((l) => l.productId)) {
        const max = getMax(l.productId);
        if (max !== 9999 && l.quantity > max) {
          const prod = allProds.find((p) => p.id === l.productId);
          message.error(`${prod?.name}: only ${max} in stock`);
          return;
        }
      }

      await sales.create({
        ...vals,
        saleDate: vals.saleDate?.format("YYYY-MM-DD"),
        items: lines.filter((l) => l.productId).map((l) => ({
          productId:  l.productId,
          quantity:   l.quantity,
          rate:       l.rate,
          taxPercent: l.taxPercent || 0,
          discount:   l.discount || 0,
        })),
      });
      message.success("Sale created");
      setModal(false);
      load();
      // Refresh stock
      inventory.stock().then((r) => {
        const qtyMap = {};
        (r.data.data.saleProducts || []).forEach((p) => {
          qtyMap[p.productId] = Number(p.stock ?? 0);
        });
        setAvailQty(qtyMap);
      });
    } catch {
      message.error("Error saving sale");
    }
  };

  // --- View ---
  const openView = async (rec) => {
    try {
      const r = await sales.get(rec.id);
      setViewRec(r.data.data);
    } catch {
      setViewRec(rec);
    }
  };

  const openReturn = async (rec) => {
    try {
      const r = await sales.get(rec.id);
      const s = r.data.data;
      setReturnModal(s);
      retForm.setFieldsValue({
        customerId:  s.customer?.id,
        returnDate:  dayjs(),
        destination: "WAREHOUSE",
        refundMode:  "CASH",
      });
      setReturnLines(
        (s.items || []).map((i) => ({
          saleItemId:  i.id,
          productName: i.product?.name,
          unit:        i.product?.unit,
          qtyOriginal: Number(i.quantity),
          rate:        Number(i.finalRate),
          taxPercent:  Number(i.taxPercent || 0),
          qtyReturn:   0,
          max:         Number(i.quantity),
        })),
      );
    } catch {
      message.error("Failed to load sale details");
    }
  };

  const onSaveReturn = async () => {
    try {
      const vals = await retForm.validateFields();
      const returning = returnLines.filter((l) => l.qtyReturn > 0);
      if (returning.length === 0) {
        message.error("Enter return qty for at least one item");
        return;
      }
      await sales.createReturn({
        customerId:  vals.customerId,
        saleId:      returnModal?.id,
        returnDate:  vals.returnDate?.format("YYYY-MM-DD"),
        destination: vals.destination,
        refundMode:  vals.refundMode,
        reason:      vals.reason,
        notes:       vals.notes,
        items:       returning.map((l) => ({
          saleItemId: l.saleItemId,
          quantity:   l.qtyReturn,
        })),
      });
      message.success("Credit note issued successfully");
      setReturnModal(null);
      load();
    } catch {
      message.error("Error processing credit note");
    }
  };

  const cols = [
    {
      title: "Sale No",
      dataIndex: "saleNo",
      render: (v) => <span className="mono text-info">{v}</span>,
    },
    { title: "Customer", dataIndex: ["customer", "name"] },
    { title: "Date", dataIndex: "saleDate", render: fmtDate },
    {
      title: "Total",
      dataIndex: "total",
      render: (v) => <span className="mono">{fmtCurrency(v)}</span>,
    },
    {
      title: "Paid",
      dataIndex: "paidAmount",
      render: (v) => (
        <span className="mono text-success">{fmtCurrency(v)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => <StatusBadge status={v} />,
    },
    {
      title: "Actions",
      key: "a",
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openView(r)}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<RollbackOutlined />}
            onClick={() => openReturn(r)}
          >
            Return
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Sales"
        subtitle="Sell items to customers"
        actions={[
          <Input.Search
            key="s"
            placeholder="Search..."
            style={{ width: 200 }}
            onSearch={setQ}
            allowClear
          />,
          <Button
            key="a"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({ saleDate: dayjs() });
              setLines([
                { productId: null, quantity: 1, rate: 0, taxPercent: 0, discount: 0 },
              ]);
              setModal(true);
            }}
          >
            New Sale
          </Button>,
        ]}
      />

      <Table
        scroll={{ x: "max-content" }}
        columns={cols}
        dataSource={data.content}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          total: data.totalElements,
          pageSize: 20,
          current: page + 1,
          onChange: (p) => setPage(p - 1),
        }}
      />

      {/* New Sale Modal */}
      <Modal
        open={modal}
        title="New Sale"
        onCancel={() => setModal(false)}
        onOk={onSave}
        width={900}
        okText="Save Sale"
      >
        <Form form={form} layout="vertical">
          <Space>
            <Form.Item name="customerId" label="Customer" rules={[{ required: true }]}>
              <Select
                showSearch
                placeholder="Select customer"
                style={{ width: 260 }}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                options={allCusts.map((c) => ({
                  value: c.id,
                  label: `${c.name} (${c.phone})`,
                }))}
              />
            </Form.Item>
            <Form.Item name="saleDate" label="Date">
              <DatePicker defaultValue={dayjs()} />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes">
            <Input />
          </Form.Item>
        </Form>

        <Divider style={{ borderColor: "var(--border)" }}>Items</Divider>
        {lines.map((l, i) => {
          const max = getMax(l.productId);
          const over = l.productId && l.quantity > max && max !== 9999;
          // Products already selected in other lines (prevent duplicates)
          const otherSelected = lines
            .filter((_, idx) => idx !== i)
            .map((r) => r.productId)
            .filter(Boolean);
          return (
            <div key={i} style={{ marginBottom: 10 }}>
              <Space align="start" wrap style={{ rowGap: 6 }}>
                <div>
                  <Select
                    showSearch
                    placeholder="Product"
                    style={{ width: 230 }}
                    value={l.productId}
                    onChange={(v) => updateLine(i, "productId", v)}
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                    options={allProds.map((p) => ({
                      value: p.id,
                      label: `${p.name} (${p.unit}) — avail: ${availQty[p.id] ?? "?"}`,
                      disabled: otherSelected.includes(p.id),
                    }))}
                  />
                </div>
                <div>
                  <InputNumber
                    placeholder="Qty"
                    min={0.001}
                    step={0.5}
                    max={max !== 9999 ? max : undefined}
                    value={l.quantity}
                    onChange={(v) => updateLine(i, "quantity", v)}
                    style={{ width: 90, borderColor: over ? "red" : undefined }}
                  />
                  {l.productId && (
                    <div style={{ fontSize: 11, color: over ? "red" : "var(--text-muted)", marginTop: 2 }}>
                      {max === 9999 ? "" : over ? `⚠ Only ${max} in stock` : `Max: ${max}`}
                    </div>
                  )}
                </div>
                <InputNumber
                  placeholder="Rate ₹" min={0} prefix="₹"
                  value={l.rate} onChange={(v) => updateLine(i, "rate", v)}
                  style={{ width: 110 }}
                />
                <InputNumber
                  placeholder="Tax %" min={0} max={100}
                  value={l.taxPercent} onChange={(v) => updateLine(i, "taxPercent", v)}
                  style={{ width: 85 }} addonAfter="%"
                />
                <InputNumber
                  placeholder="Discount" min={0} prefix="₹"
                  value={l.discount} onChange={(v) => updateLine(i, "discount", v)}
                  style={{ width: 110 }}
                />
                <span className="mono" style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 80 }}>
                  = {fmtCurrency(lineAmount(l))}
                </span>
                {lines.length > 1 && (
                  <Button icon={<DeleteOutlined />} size="small" danger
                    onClick={() => setLines(lines.filter((_, idx) => idx !== i))} />
                )}
              </Space>
            </div>
          );
        })}
        <Button
          icon={<PlusOutlined />}
          size="small"
          onClick={() =>
            setLines([
              ...lines,
              { productId: null, quantity: 1, rate: 0, taxPercent: 0, discount: 0 },
            ])
          }
        >
          Add Item
        </Button>
        <Divider style={{ borderColor: "var(--border)" }} />
        <div
          style={{
            textAlign: "right",
            fontFamily: "var(--font-mono)",
            fontSize: 16,
          }}
        >
          Total:{" "}
          <strong style={{ color: "var(--accent)" }}>
            {fmtCurrency(total)}
          </strong>
        </div>
      </Modal>

      {/* View Drawer */}
      <Drawer
        title={`Sale: ${viewRec?.saleNo}`}
        open={!!viewRec}
        onClose={() => setViewRec(null)}
        width={560}
      >
        {viewRec && (
          <>
            <Descriptions
              bordered
              size="small"
              column={2}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Sale No" span={2}>
                <span className="mono">{viewRec.saleNo}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Customer" span={2}>
                <strong>{viewRec.customer?.name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {fmtDate(viewRec.saleDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <StatusBadge status={viewRec.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Total">
                <span className="mono">{fmtCurrency(viewRec.total)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Paid">
                <span className="mono text-success">
                  {fmtCurrency(viewRec.paidAmount)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Balance" span={2}>
                <span
                  className="mono"
                  style={{ color: "var(--danger)", fontWeight: 700 }}
                >
                  {fmtCurrency(
                    (viewRec.total || 0) - (viewRec.paidAmount || 0),
                  )}
                </span>
              </Descriptions.Item>
              {viewRec.notes && (
                <Descriptions.Item label="Notes" span={2}>
                  {viewRec.notes}
                </Descriptions.Item>
              )}
            </Descriptions>
            <Divider orientation="left">Sale Items</Divider>
            <Table
              scroll={{ x: "max-content" }}
              size="small"
              pagination={false}
              rowKey="id"
              dataSource={viewRec.items || []}
              columns={[
                { title: "Product", dataIndex: ["product", "name"] },
                {
                  title: "Unit",
                  dataIndex: ["product", "unit"],
                  render: (v) => <Tag>{v}</Tag>,
                },
                { title: "Qty", dataIndex: "quantity" },
                {
                  title: "Rate",
                  dataIndex: "rate",
                  render: (v) => fmtCurrency(v),
                },
                {
                  title: "Discount",
                  dataIndex: "discount",
                  render: (v) => (v > 0 ? `-${fmtCurrency(v)}` : "—"),
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  render: (v) => <span className="mono">{fmtCurrency(v)}</span>,
                },
              ]}
            />
          </>
        )}
      </Drawer>

      {/* Credit Note / Sale Return Modal */}
      <Modal
        open={!!returnModal}
        title={
          <Space>
            <FileTextOutlined style={{ color: "var(--info)" }} />
            Credit Note — {returnModal?.saleNo}
          </Space>
        }
        onCancel={() => setReturnModal(null)}
        onOk={onSaveReturn}
        width={720}
        okText="Issue Credit Note"
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="GST Credit Note"
          description={
            <span style={{ fontSize: 12 }}>
              Under <strong>Section 34 of CGST Act</strong>, you must issue a Credit Note for returns — do not cancel the original GST invoice.
              The credit note will reduce your GST liability in <strong>GSTR-1 &amp; GSTR-3B</strong>.
            </span>
          }
        />
        <Form form={retForm} layout="vertical">
          <Space wrap>
            <Form.Item name="customerId" label="Customer">
              <Select disabled style={{ width: 220 }}
                options={allCusts.map((c) => ({ value: c.id, label: c.name }))} />
            </Form.Item>
            <Form.Item name="returnDate" label="Credit Note Date" rules={[{ required: true }]}>
              <DatePicker defaultValue={dayjs()} />
            </Form.Item>
            <Form.Item name="refundMode" label="Refund Mode" rules={[{ required: true }]}>
              <Select style={{ width: 140 }} options={[
                { value: "CASH",   label: "💵 Cash" },
                { value: "UPI",    label: "📱 UPI" },
                { value: "BANK",   label: "🏦 Bank Transfer" },
                { value: "ADJUST", label: "🔄 Adjust in Future" },
              ]} />
            </Form.Item>
            <Form.Item name="destination" label="Return Destination" rules={[{ required: true }]}>
              <Select style={{ width: 140 }} options={[
                { value: "WAREHOUSE", label: "📦 Warehouse" },
                { value: "SCRAP",     label: "🗑 Scrap" },
              ]} />
            </Form.Item>
          </Space>
          <Form.Item name="reason" label="Return Reason" rules={[{ required: true }]}>
            <Select options={[
              { value: "DEFECTIVE",     label: "Defective / Damaged" },
              { value: "WRONG_ITEM",    label: "Wrong item delivered" },
              { value: "CUSTOMER_CHOICE", label: "Customer changed mind" },
              { value: "QUALITY",       label: "Quality not as expected" },
              { value: "OTHER",         label: "Other" },
            ]} placeholder="Select reason" />
          </Form.Item>
          <Form.Item name="notes" label="Additional Notes">
            <Input placeholder="e.g. Customer returned product in original packaging" />
          </Form.Item>
        </Form>

        <Divider>Items Being Returned</Divider>
        {returnLines.map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10,
            padding: "8px 12px", background: "var(--surface2)", borderRadius: 6,
            border: "1px solid var(--border)" }}>
            <div style={{ minWidth: 160, fontFamily: "var(--font-mono)", fontSize: 13 }}>
              {l.productName}
            </div>
            <Tag>{l.unit}</Tag>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Sold: {l.qtyOriginal}</div>
            <InputNumber
              min={0} max={l.max} step={0.5}
              placeholder="Return qty"
              value={l.qtyReturn}
              onChange={(v) => {
                const copy = [...returnLines];
                copy[i].qtyReturn = v ?? 0;
                setReturnLines(copy);
              }}
              style={{ width: 110 }}
            />
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>max {l.max}</span>
            {l.qtyReturn > 0 && (
              <div style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: 12 }}>
                <div style={{ color: "var(--success)" }}>
                  Refund: {fmtCurrency(l.qtyReturn * l.rate)}
                </div>
                {l.taxPercent > 0 && (
                  <div style={{ color: "var(--text-muted)", fontSize: 11 }}>
                    GST ({l.taxPercent}%): {fmtCurrency(l.qtyReturn * l.rate * l.taxPercent / 100)}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Credit Note Summary */}
        {returnLines.some((l) => l.qtyReturn > 0) && (() => {
          const baseAmt = returnLines.reduce((s, l) => s + l.qtyReturn * l.rate, 0);
          const gstAmt  = returnLines.reduce((s, l) => s + l.qtyReturn * l.rate * (l.taxPercent || 0) / 100, 0);
          return (
            <div style={{ background: "var(--surface2)", borderRadius: 6, padding: "12px 16px",
              border: "1px solid var(--border)", marginTop: 8, fontFamily: "var(--font-mono)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                <span>Base Value:</span><span>{fmtCurrency(baseAmt)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                <span>GST Adjustment:</span><span style={{ color: "var(--success)" }}>-{fmtCurrency(gstAmt)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700,
                borderTop: "1px solid var(--border)", paddingTop: 6, marginTop: 4 }}>
                <span>Total Credit Note Value:</span>
                <span style={{ color: "var(--accent)" }}>{fmtCurrency(baseAmt + gstAmt)}</span>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}